import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Calendar as CalendarIcon, MessageSquare, LogOut, Plus, 
  Pencil, Trash2, Check, X, Clock, Wrench, Upload, Image as ImageIcon,
  Archive, ArchiveRestore, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIVISIONS = [
  { value: 'equipment_rental', label: 'Equipment Rental' },
  { value: 'work_site', label: 'Work-Site' },
  { value: 'transport', label: 'Transport' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'excavation', label: 'Excavation' },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ equipment_count: 0, rental_count: 0, pending_rentals: 0, contact_count: 0 });
  const [equipment, setEquipment] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('equipment');
  const fileInputRef = useRef(null);
  
  // Predefined categories
  const EQUIPMENT_CATEGORIES = [
    'Concrete',
    'Demolition',
    'Construction',
    'Shoring',
    'Heavy Equipment',
    'Aerial Equipment',
    'Transport',
    'Power and Lighting',
    'Landscaping',
    'General Tools'
  ];
  
  // Equipment form state
  const [equipmentForm, setEquipmentForm] = useState({
    name: '', subheading: '', categories: [], description: '', daily_rate: '', weekly_rate: '', monthly_rate: '',
    available: true, division: 'equipment_rental', images: [], accessories: []
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Rental form state
  const [rentalForm, setRentalForm] = useState({
    equipment_id: '', customer_name: '', customer_email: '', customer_phone: '',
    start_date: '', end_date: '', notes: ''
  });
  const [rentalDialogOpen, setRentalDialogOpen] = useState(false);

  const token = localStorage.getItem('admin_token');

  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    verifyAndFetch();
  }, [token, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [showArchived]);

  const verifyAndFetch = async () => {
    try {
      await axios.get(`${API}/admin/verify`, authHeaders);
      await fetchData();
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, equipmentRes, rentalsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/equipment`),
        axios.get(`${API}/admin/rentals`, authHeaders),
        axios.get(`${API}/admin/contacts?include_archived=${showArchived}`, authHeaders)
      ]);
      setStats(statsRes.data);
      setEquipment(equipmentRes.data);
      setRentals(rentalsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveContact = async (contactId, isArchived) => {
    try {
      const endpoint = isArchived ? 'unarchive' : 'archive';
      await axios.patch(`${API}/admin/contacts/${contactId}/${endpoint}`, {}, authHeaders);
      toast.success(isArchived ? 'Message restored' : 'Message archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to permanently delete this message?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, authHeaders);
      toast.success('Message deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin');
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newImages = [...uploadedImages];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/admin/upload`, formData, {
          headers: { 
            ...authHeaders.headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        newImages.push(response.data.url);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploadedImages(newImages);
    setUploading(false);
    
    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    const newImages = [...uploadedImages];
    newImages.splice(index, 1);
    setUploadedImages(newImages);
  };

  // Drag and drop handlers for image reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    const newImages = [...uploadedImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    setUploadedImages(newImages);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Equipment handlers
  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;
    
    // Validate minimum 3 images for new equipment
    if (!editingEquipment && uploadedImages.length < 3) {
      toast.error('Please upload at least 3 images');
      return;
    }

    setSubmitting(true);
    
    try {
      const data = {
        name: equipmentForm.name,
        subheading: equipmentForm.subheading || '',
        category: equipmentForm.categories?.[0] || '',  // Legacy: first category
        categories: equipmentForm.categories || [],
        description: equipmentForm.description,
        daily_rate: parseFloat(equipmentForm.daily_rate),
        weekly_rate: parseFloat(equipmentForm.weekly_rate),
        monthly_rate: parseFloat(equipmentForm.monthly_rate) || 0,
        available: equipmentForm.available,
        division: equipmentForm.division,
        image_url: uploadedImages[0] || equipmentForm.image_url || '',
        images: uploadedImages.length > 0 ? uploadedImages : equipmentForm.images,
        accessories: equipmentForm.accessories || []
      };

      if (editingEquipment) {
        await axios.put(`${API}/admin/equipment/${editingEquipment.id}`, data, authHeaders);
        toast.success('Equipment updated!');
      } else {
        await axios.post(`${API}/admin/equipment`, data, authHeaders);
        toast.success('Equipment added!');
      }
      setEquipmentDialogOpen(false);
      resetEquipmentForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save equipment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) return;
    try {
      await axios.delete(`${API}/admin/equipment/${id}`, authHeaders);
      toast.success('Equipment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete equipment');
    }
  };

  const openEditEquipment = (item) => {
    setEditingEquipment(item);
    // Support both legacy single category and new categories array
    const categories = item.categories?.length > 0 
      ? item.categories 
      : (item.category ? [item.category] : []);
    setEquipmentForm({
      name: item.name,
      subheading: item.subheading || '',
      categories: categories,
      description: item.description,
      daily_rate: item.daily_rate.toString(),
      weekly_rate: item.weekly_rate.toString(),
      monthly_rate: item.monthly_rate?.toString() || '',
      available: item.available,
      division: item.division,
      image_url: item.image_url,
      images: item.images || [],
      accessories: item.accessories || []
    });
    setUploadedImages(item.images || (item.image_url ? [item.image_url] : []));
    setEquipmentDialogOpen(true);
  };

  const resetEquipmentForm = () => {
    setEquipmentForm({
      name: '', subheading: '', categories: [], description: '', daily_rate: '', weekly_rate: '', monthly_rate: '',
      available: true, division: 'equipment_rental', images: [], accessories: []
    });
    setUploadedImages([]);
    setEditingEquipment(null);
  };

  // Rental handlers
  const handleRentalSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/rentals`, rentalForm, authHeaders);
      toast.success('Rental created!');
      setRentalDialogOpen(false);
      setRentalForm({
        equipment_id: '', customer_name: '', customer_email: '', customer_phone: '',
        start_date: '', end_date: '', notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create rental');
    }
  };

  const updateRentalStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/rentals/${id}`, { status }, authHeaders);
      toast.success(`Rental ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update rental');
    }
  };

  const handleDeleteRental = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rental?')) return;
    try {
      await axios.delete(`${API}/admin/rentals/${id}`, authHeaders);
      toast.success('Rental deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rental');
    }
  };

  // Get display image for equipment
  const getEquipmentImage = (item) => {
    if (item.images && item.images.length > 0) {
      return item.images[0].startsWith('/api') ? `${BACKEND_URL}${item.images[0]}` : item.images[0];
    }
    return item.image_url;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20 pt-24 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light tracking-tight">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage equipment, rentals, and inquiries</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="rounded-full">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border/60 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-semibold">{stats.equipment_count}</p>
                <p className="text-sm text-muted-foreground">Equipment</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border/60 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <CalendarIcon className="h-8 w-8 text-secondary" />
              <div>
                <p className="text-2xl font-semibold">{stats.rental_count}</p>
                <p className="text-sm text-muted-foreground">Total Rentals</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border/60 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-accent" />
              <div>
                <p className="text-2xl font-semibold">{stats.pending_rentals}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border/60 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-semibold">{stats.contact_count}</p>
                <p className="text-sm text-muted-foreground">Messages</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="equipment" className="flex items-center gap-2">
              <Wrench className="h-4 w-4" /> Equipment
            </TabsTrigger>
            <TabsTrigger value="rentals" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" /> Rentals
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> Messages
            </TabsTrigger>
          </TabsList>

          {/* Equipment Tab */}
          <TabsContent value="equipment">
            <div className="bg-card border border-border/60 rounded-lg">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-medium">Equipment Inventory</h2>
                <div className="flex gap-2">
                  <Dialog open={equipmentDialogOpen} onOpenChange={(open) => {
                    // Only allow closing via the X button or Cancel/Submit
                    if (!open && equipmentDialogOpen) return;
                    setEquipmentDialogOpen(open);
                    if (!open) resetEquipmentForm();
                  }}>
                    <DialogTrigger asChild>
                      <Button className="rounded-full" data-testid="add-equipment-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add Equipment
                      </Button>
                    </DialogTrigger>
                    <DialogContent 
                      className="max-w-lg max-h-[90vh] overflow-y-auto"
                      onInteractOutside={(e) => e.preventDefault()}
                      onEscapeKeyDown={(e) => e.preventDefault()}
                      onCloseClick={() => {
                        setEquipmentDialogOpen(false);
                        resetEquipmentForm();
                      }}
                    >
                      <DialogHeader>
                        <DialogTitle>{editingEquipment ? 'Edit Equipment' : 'Add New Equipment'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleEquipmentSubmit} className="space-y-4 mt-4">
                        <div>
                          <Label>Name</Label>
                          <Input
                            value={equipmentForm.name}
                            onChange={(e) => setEquipmentForm({...equipmentForm, name: e.target.value})}
                            required
                        />
                      </div>
                      <div>
                        <Label>Subheading (e.g., model number, brand)</Label>
                        <Input
                          value={equipmentForm.subheading}
                          onChange={(e) => setEquipmentForm({...equipmentForm, subheading: e.target.value})}
                          placeholder="Milwaukee MXF315-2XC"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Categories</Label>
                          <Select
                            value=""
                            onValueChange={(v) => {
                              if (v && !equipmentForm.categories.includes(v)) {
                                setEquipmentForm({...equipmentForm, categories: [...equipmentForm.categories, v]});
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select categories..." />
                            </SelectTrigger>
                            <SelectContent>
                              {EQUIPMENT_CATEGORIES
                                .filter(cat => !equipmentForm.categories.includes(cat))
                                .map((cat) => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))
                              }
                            </SelectContent>
                          </Select>
                          {equipmentForm.categories.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {equipmentForm.categories.map(cat => (
                                <Badge key={cat} variant="secondary" className="flex items-center gap-1 text-xs">
                                  {cat}
                                  <button
                                    type="button"
                                    onClick={() => setEquipmentForm({
                                      ...equipmentForm,
                                      categories: equipmentForm.categories.filter(c => c !== cat)
                                    })}
                                    className="ml-1 hover:text-destructive"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <Label>Division</Label>
                          <Select 
                            value={equipmentForm.division} 
                            onValueChange={(v) => setEquipmentForm({...equipmentForm, division: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DIVISIONS.map((d) => (
                                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={equipmentForm.description}
                          onChange={(e) => setEquipmentForm({...equipmentForm, description: e.target.value})}
                          required
                          rows={6}
                          placeholder="Enter description...

Use line breaks for paragraphs.
• Use bullet points with • or -
- Like this"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Line breaks and bullet points (• or -) will be preserved
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Daily Rate ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={equipmentForm.daily_rate}
                            onChange={(e) => setEquipmentForm({...equipmentForm, daily_rate: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label>Weekly Rate ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={equipmentForm.weekly_rate}
                            onChange={(e) => setEquipmentForm({...equipmentForm, weekly_rate: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label>Monthly Rate ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={equipmentForm.monthly_rate}
                            onChange={(e) => setEquipmentForm({...equipmentForm, monthly_rate: e.target.value})}
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                      
                      {/* Image Upload Section */}
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <ImageIcon className="h-4 w-4" />
                          Images (minimum 3 required)
                        </Label>
                        
                        {/* Upload Button */}
                        <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            id="image-upload"
                          />
                          <label 
                            htmlFor="image-upload" 
                            className="cursor-pointer flex flex-col items-center gap-2"
                          >
                            <Upload className="h-8 w-8 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {uploading ? 'Uploading...' : 'Click to upload images'}
                            </span>
                          </label>
                        </div>

                        {/* Uploaded Images Preview - Draggable */}
                        {uploadedImages.length > 0 && (
                          <>
                            <p className="text-xs text-muted-foreground mt-4 mb-2">
                              Drag images to reorder. First image is the main photo.
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                              {uploadedImages.map((url, index) => (
                                <div 
                                  key={url} 
                                  className={`relative aspect-square rounded-lg overflow-hidden bg-muted cursor-move transition-all ${
                                    draggedIndex === index ? 'opacity-50 scale-95' : ''
                                  } ${draggedIndex !== null && draggedIndex !== index ? 'hover:ring-2 hover:ring-primary' : ''}`}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, index)}
                                  onDragOver={(e) => handleDragOver(e, index)}
                                  onDrop={(e) => handleDrop(e, index)}
                                  onDragEnd={handleDragEnd}
                                >
                                  <img 
                                    src={url.startsWith('/api') ? `${BACKEND_URL}${url}` : url}
                                    alt={`Upload ${index + 1}`}
                                    className="w-full h-full object-cover pointer-events-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="absolute top-1 right-1 p-1 bg-destructive text-white rounded-full hover:bg-destructive/80 z-10"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                  {index === 0 && (
                                    <span className="absolute bottom-1 left-1 text-xs bg-primary text-white px-2 py-0.5 rounded">
                                      Main
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        
                        <p className="text-xs text-muted-foreground mt-2">
                          {uploadedImages.length}/3 minimum images uploaded
                        </p>
                      </div>

                      {/* Suggested Accessories */}
                      <div>
                        <Label className="mb-2 block">Suggested Accessories (optional)</Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Select up to 3 items to show as suggested accessories. If none selected, similar items will be shown automatically.
                        </p>
                        <Select
                          value=""
                          onValueChange={(v) => {
                            if (v && !equipmentForm.accessories.includes(v) && equipmentForm.accessories.length < 3) {
                              setEquipmentForm({...equipmentForm, accessories: [...equipmentForm.accessories, v]});
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select accessories..." />
                          </SelectTrigger>
                          <SelectContent>
                            {equipment
                              .filter(e => e.id !== editingEquipment?.id && !equipmentForm.accessories.includes(e.id))
                              .map(e => (
                                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                        {equipmentForm.accessories.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {equipmentForm.accessories.map(accId => {
                              const acc = equipment.find(e => e.id === accId);
                              return acc ? (
                                <Badge key={accId} variant="secondary" className="flex items-center gap-1">
                                  {acc.name}
                                  <button
                                    type="button"
                                    onClick={() => setEquipmentForm({
                                      ...equipmentForm,
                                      accessories: equipmentForm.accessories.filter(id => id !== accId)
                                    })}
                                    className="ml-1 hover:text-destructive"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={equipmentForm.available}
                          onChange={(e) => setEquipmentForm({...equipmentForm, available: e.target.checked})}
                          id="available"
                        />
                        <Label htmlFor="available">Available for rent</Label>
                      </div>
                      <div className="flex gap-3">
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="flex-1 rounded-full"
                          disabled={submitting}
                          onClick={() => {
                            setEquipmentDialogOpen(false);
                            resetEquipmentForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="flex-1 rounded-full" disabled={uploading || submitting}>
                          {submitting ? 'Saving...' : (editingEquipment ? 'Update Equipment' : 'Add Equipment')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-medium">Equipment</th>
                      <th className="text-left p-4 font-medium">Division</th>
                      <th className="text-left p-4 font-medium">Daily</th>
                      <th className="text-left p-4 font-medium">Weekly</th>
                      <th className="text-left p-4 font-medium">Images</th>
                      <th className="text-left p-4 font-medium">Status</th>
                      <th className="text-left p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment.map((item) => (
                      <tr key={item.id} className="border-t border-border">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img 
                              src={getEquipmentImage(item)} 
                              alt={item.name} 
                              className="w-12 h-12 object-cover rounded" 
                            />
                            <div>
                              <p className="font-medium">{item.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {item.categories?.length > 0 ? item.categories.join(', ') : item.category}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline">
                            {DIVISIONS.find(d => d.value === item.division)?.label || item.division}
                          </Badge>
                        </td>
                        <td className="p-4">${item.daily_rate.toFixed(2)}</td>
                        <td className="p-4">${item.weekly_rate.toFixed(2)}</td>
                        <td className="p-4">
                          <Badge variant="secondary">
                            {(item.images?.length || (item.image_url ? 1 : 0))} photo{(item.images?.length || 1) !== 1 ? 's' : ''}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <Badge variant={item.available ? 'default' : 'secondary'}>
                            {item.available ? 'Available' : 'Unavailable'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditEquipment(item)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteEquipment(item.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Rentals Tab */}
          <TabsContent value="rentals">
            <div className="bg-card border border-border/60 rounded-lg">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-medium">Rental Bookings</h2>
                <Dialog open={rentalDialogOpen} onOpenChange={setRentalDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" data-testid="add-rental-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add Rental
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create New Rental</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleRentalSubmit} className="space-y-4 mt-4">
                      <div>
                        <Label>Equipment</Label>
                        <Select 
                          value={rentalForm.equipment_id} 
                          onValueChange={(v) => setRentalForm({...rentalForm, equipment_id: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select equipment" />
                          </SelectTrigger>
                          <SelectContent>
                            {equipment.map((eq) => (
                              <SelectItem key={eq.id} value={eq.id}>{eq.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Customer Name</Label>
                        <Input
                          value={rentalForm.customer_name}
                          onChange={(e) => setRentalForm({...rentalForm, customer_name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={rentalForm.customer_email}
                            onChange={(e) => setRentalForm({...rentalForm, customer_email: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            type="tel"
                            value={rentalForm.customer_phone}
                            onChange={(e) => setRentalForm({...rentalForm, customer_phone: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={rentalForm.start_date}
                            onChange={(e) => setRentalForm({...rentalForm, start_date: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={rentalForm.end_date}
                            onChange={(e) => setRentalForm({...rentalForm, end_date: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <Textarea
                          value={rentalForm.notes}
                          onChange={(e) => setRentalForm({...rentalForm, notes: e.target.value})}
                        />
                      </div>
                      <Button type="submit" className="w-full rounded-full">Create Rental</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-medium">Equipment</th>
                      <th className="text-left p-4 font-medium">Customer</th>
                      <th className="text-left p-4 font-medium">Dates</th>
                      <th className="text-left p-4 font-medium">Status</th>
                      <th className="text-left p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rentals.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          No rentals yet
                        </td>
                      </tr>
                    ) : (
                      rentals.map((rental) => (
                        <tr key={rental.id} className="border-t border-border">
                          <td className="p-4 font-medium">{rental.equipment_name}</td>
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{rental.customer_name}</p>
                              <p className="text-sm text-muted-foreground">{rental.customer_email}</p>
                              {rental.customer_phone && (
                                <p className="text-sm text-muted-foreground">{rental.customer_phone}</p>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            {rental.start_date && rental.end_date ? (
                              <span>{rental.start_date} → {rental.end_date}</span>
                            ) : (
                              <span className="text-muted-foreground">General inquiry</span>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge variant={
                              rental.status === 'confirmed' ? 'default' :
                              rental.status === 'pending' ? 'secondary' :
                              rental.status === 'completed' ? 'outline' : 'destructive'
                            }>
                              {rental.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              {rental.status === 'pending' && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => updateRentalStatus(rental.id, 'confirmed')} title="Confirm">
                                    <Check className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => updateRentalStatus(rental.id, 'cancelled')} title="Cancel">
                                    <X className="h-4 w-4 text-red-600" />
                                  </Button>
                                </>
                              )}
                              {rental.status === 'confirmed' && (
                                <Button variant="ghost" size="sm" onClick={() => updateRentalStatus(rental.id, 'completed')} title="Complete">
                                  <Check className="h-4 w-4" />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteRental(rental.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="bg-card border border-border/60 rounded-lg">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-medium">Contact Messages</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowArchived(!showArchived)}
                  className="gap-2"
                  data-testid="toggle-archived-btn"
                >
                  {showArchived ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showArchived ? 'Hide Archived' : 'Show Archived'}
                </Button>
              </div>
              
              <div className="divide-y divide-border">
                {contacts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {showArchived ? 'No messages found' : 'No messages yet'}
                  </div>
                ) : (
                  contacts.map((msg) => (
                    <div key={msg.id} className={`p-6 ${msg.archived ? 'bg-muted/30' : ''}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{msg.name}</p>
                            {msg.archived && <Badge variant="secondary" className="text-xs">Archived</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">{msg.email} {msg.phone && `• ${msg.phone}`}</p>
                          {msg.service && <Badge variant="outline" className="mt-2">{msg.service}</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-muted-foreground whitespace-nowrap mr-2">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleArchiveContact(msg.id, msg.archived)}
                            title={msg.archived ? 'Restore' : 'Archive'}
                            data-testid={`archive-btn-${msg.id}`}
                          >
                            {msg.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteContact(msg.id)}
                            className="text-destructive hover:text-destructive"
                            title="Delete permanently"
                            data-testid={`delete-btn-${msg.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="mt-4 text-muted-foreground">{msg.message}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
